import React, { Suspense, lazy, useEffect } from "react";
import { setCanonical } from "@/lib/seo";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { KeystoneOnboardingProvider } from "@/lib/keystone-onboarding";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import PartnerSystem from "@/pages/PartnerSystem";

// Lazy-loaded routes — kept out of the initial bundle to speed up LCP/FCP on the two
// primary entry pages (Home + Partner System). Each will code-split into its own chunk.
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const SmartInvesting = lazy(() => import("@/pages/SmartInvesting"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ProtectedRoute = lazy(() => import("@/components/ProtectedRoute"));

// Lightweight fallback — no layout shift, matches dark theme
const RouteFallback = () => (
    <div className="min-h-[60vh] bg-ink" aria-hidden="true" />
);

function Shell() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    useEffect(() => {
        setCanonical(window.location.origin + location.pathname);
    }, [location.pathname]);
    return (
        <div className="bg-ink min-h-screen text-cream">
            {!isAdmin && <Header />}
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/smart-investing" element={<SmartInvesting />} />
                    <Route path="/partner-system" element={<PartnerSystem />} />
                    <Route path="/category/:slug" element={<CategoryPage />} />
                    <Route path="/product/:slug" element={<ProductPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Suspense>
            {!isAdmin && <Footer />}
            <Toaster
                theme="dark"
                richColors
                position="top-right"
                toastOptions={{
                    style: {
                        background: "#141311",
                        border: "1px solid rgba(212,175,55,0.3)",
                        color: "#F8F6F0",
                    },
                }}
            />
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <KeystoneOnboardingProvider>
                    <Shell />
                </KeystoneOnboardingProvider>
            </BrowserRouter>
        </AuthProvider>
    );
}
